import { Fragment, type ReactNode } from 'react';
import {
  CoachStatusEnum,
  EventCompetitivenessEnum,
  EventTypesEnum,
  NotificationStatusesEnum,
  SportsEnum,
} from 'types/generated/client';
import { type GameTitleSource, buildGameTitle } from 'utils/shared/games/buildGameTitle';
import { formatVenueDateParts } from 'utils/shared/time/formatVenueTimeRange';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import CoachBadge from 'svg/CoachBadge';
import Crown from 'svg/Crown';
import Location from 'svg/Location';
import RequesterInfo from 'screens/Find/components/RequestToPlay/RequesterInfo';
import ActivityBadge from 'components/ActivityBadge';
import Dot from 'components/Dot';
import { mapCompetitiveness } from 'components/Find/mapCompetitiveness';
import classNames from 'styles/utils/classNames';
import CardNotification from './CardNotification';

/** Light tint background + saturated text, matching the Figma "Badge notification" tints. */
export type NotificationPillTone = 'accepted' | 'declined' | 'cancelled' | 'received';

const PILL_TONE_CLASSES: Record<NotificationPillTone, string> = {
  accepted: 'bg-color-success-background text-color-success',
  declined: 'bg-color-bg-lightmode-error text-color-text-lightmode-error',
  // No semantic "warning" token exists — mapped to the brand-yellow tint (Figma Yellow/100 + Yellow text).
  cancelled: 'bg-brand-yellow-100 text-brand-yellow-600',
  // No dedicated design — mirrors the tinted-pill style with the brand-fire tokens.
  received: 'bg-color-bg-lightmode-brand-secondary text-color-text-brand',
};

interface NotificationGameEvent {
  sport: SportsEnum;
  startDateTime?: string | null;
  endDateTime?: string | null;
  timezoneName?: string | null;
  venue?: { timezone?: string | null } | null;
  groups?: (GameTitleSource & { competitiveness?: EventCompetitivenessEnum | null })[] | null;
}

interface NotificationPlayerProfile {
  fullName?: string | null;
  preferredName?: string | null;
  username?: string | null;
  profileImagePath?: string | null;
  cityName?: string | null;
  activeCity?: { name: string } | null;
}

/** The bordered game card: activity badge, composed title, venue-local date and time. */
export function NotificationGameCard({ event }: { event: NotificationGameEvent }) {
  const group = event.groups?.[0];
  const competitiveness = mapCompetitiveness(group?.competitiveness);
  const dateParts =
    event.startDateTime && event.endDateTime
      ? formatVenueDateParts(event.startDateTime, event.endDateTime, resolveVenueTimeZone(event))
      : [];

  return (
    <div className="flex w-full flex-col gap-ds-sm rounded-[1.25rem] border border-color-border-card-lightmode p-ds-lg dark:border-color-border-card-darkmode">
      <ActivityBadge
        type={EventTypesEnum.OpenPlay}
        sport={event.sport}
        competitiveness={competitiveness}
        size="xs"
      />
      {group && (
        <h4 className="typography-product-subheading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {buildGameTitle(group)}
        </h4>
      )}
      {dateParts.length > 0 && (
        <div className="typography-product-text-card-mobile flex items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {dateParts.map((part, index) => (
            <Fragment key={part}>
              {index > 0 && <Dot />}
              <span>{part}</span>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

/** The bordered player card: avatar, name and city, via the shared RequesterInfo row. */
export function NotificationPlayerCard({ profile }: { profile: NotificationPlayerProfile }) {
  const city = profile.cityName || profile.activeCity?.name || undefined;

  return (
    <div className="flex w-full flex-col rounded-[1.25rem] border border-color-border-card-lightmode p-ds-lg dark:border-color-border-card-darkmode">
      <RequesterInfo
        player={{
          name: profile.fullName || profile.preferredName || 'Player',
          username: profile.username ?? undefined,
          imageUrl: getProfileImageUrlOrPlaceholder({ path: profile.profileImagePath }),
        }}
        avatarClassName="size-16"
        subLabel={city}
        subLabelIcon={
          <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
        }
      />
    </div>
  );
}

interface Props {
  status: NotificationStatusesEnum;
  actorName: string;
  /** A plain string in the generated types; compared against the enum value below. */
  actorCoachStatus?: string | null;
  pill: { label: string; tone: NotificationPillTone };
  title: string;
  message?: string | null;
  /** The game or player card under the title. */
  children?: ReactNode;
}

/** Shared shell for player-to-player notifications: actor, status pill, title, detail card. */
export default function NotificationActivityCard({
  status,
  actorName,
  actorCoachStatus,
  pill,
  title,
  message,
  children,
}: Props) {
  return (
    <CardNotification status={status}>
      <div className="flex flex-col gap-ds-sm p-ds-lg">
        <div className="flex items-center justify-between gap-ds-sm">
          <div className="flex min-w-0 items-center gap-ds-xs">
            <Crown className="h-3.5 w-3.5 shrink-0 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
            <span className="typography-product-text-card-mobile truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {actorName}
            </span>
            {actorCoachStatus === CoachStatusEnum.Active && (
              <CoachBadge className="h-4 w-4 shrink-0 text-color-brand-primary" />
            )}
          </div>
          <span
            className={classNames(
              'typography-product-button-label-xs shrink-0 whitespace-nowrap rounded-full px-2 py-0.5',
              PILL_TONE_CLASSES[pill.tone],
            )}
          >
            {pill.label}
          </span>
        </div>

        <div className="flex flex-col gap-ds-xs">
          <h3 className="typography-product-heading-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {title}
          </h3>
          {message && (
            <p className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {message}
            </p>
          )}
        </div>

        {children}
      </div>
    </CardNotification>
  );
}
